/**
 * PRD §8.4 Tier A step 1: line assembly by transform-y grouping, x-sorted,
 * single-column assumption. Calibrated against three real producers
 * (pdftex, Chrome print-to-PDF, Bedesten HTML-to-PDF); see fixture set.
 */
/** |Δy| within this fraction of the taller item's height = same visual line
 * (superscripts/footnote refs sit ~0.5 h above the baseline). */
const LINE_Y_TOLERANCE_FACTOR = 0.6;
const LINE_Y_TOLERANCE_MIN = 2;
/** Horizontal gap beyond this fraction of the item height reads as a space
 * (a space glyph is ~0.25 em; kern gaps stay well under 0.12 em). */
const SPACE_GAP_FACTOR = 0.12;
const SPACE_GAP_MIN = 0.5;
/** Whitespace ITEMS narrower than this fraction of the neighbour height are
 * typographic thin/hair spaces (e.g. pdftex's \\, in "10,000") — dropped. */
const THIN_SPACE_FACTOR = 0.25;
function yTolerance(a, b) {
    return Math.max(LINE_Y_TOLERANCE_MIN, LINE_Y_TOLERANCE_FACTOR * Math.max(a, b));
}
export function assembleLines(items, page) {
    const placed = [];
    // Baselines flagged by empty hasEOL marker items: the line AT that y starts
    // a new paragraph (consistent across all observed producers).
    const hardBreakYs = [];
    for (const item of items) {
        const x = item.transform[4] ?? 0;
        const y = item.transform[5] ?? 0;
        if (item.str.length === 0) {
            if (item.hasEOL)
                hardBreakYs.push(y);
            continue;
        }
        placed.push({ str: item.str, x, y, width: item.width, height: item.height });
    }
    if (placed.length === 0)
        return [];
    // Cluster into lines top-to-bottom; refY/refHeight track the dominant
    // (tallest) item so superscripts join their baseline neighbours.
    placed.sort((a, b) => b.y - a.y || a.x - b.x);
    const clusters = [];
    let current = [];
    let refY = 0;
    let refHeight = 0;
    for (const item of placed) {
        if (current.length === 0 || Math.abs(item.y - refY) <= yTolerance(refHeight, item.height)) {
            if (current.length === 0 || item.height > refHeight) {
                refHeight = item.height;
                refY = item.y;
            }
            current.push(item);
        }
        else {
            clusters.push(current);
            current = [item];
            refY = item.y;
            refHeight = item.height;
        }
    }
    clusters.push(current);
    const lines = [];
    for (const cluster of clusters) {
        cluster.sort((a, b) => a.x - b.x);
        let text = '';
        let prev;
        let sawWhitespaceItem = false;
        for (const item of cluster) {
            if (item.str.trim().length === 0) {
                // Producer-emitted space glyph: a word space unless thin (see below).
                sawWhitespaceItem = true;
                continue;
            }
            if (prev !== undefined) {
                const gap = item.x - (prev.x + prev.width);
                const height = Math.max(prev.height, item.height, 1);
                const isSpace = sawWhitespaceItem
                    ? gap >= THIN_SPACE_FACTOR * height
                    : gap > Math.max(SPACE_GAP_MIN, SPACE_GAP_FACTOR * height);
                if (isSpace)
                    text += ' ';
            }
            text += item.str;
            prev = item;
            sawWhitespaceItem = false;
        }
        const visible = cluster.filter((item) => item.str.trim().length > 0);
        if (visible.length === 0)
            continue;
        let dominant = visible[0];
        for (const item of visible)
            if (item.height > dominant.height)
                dominant = item;
        const x0 = Math.min(...visible.map((item) => item.x));
        const x1 = Math.max(...visible.map((item) => item.x + item.width));
        const y = dominant.y;
        lines.push({
            page,
            y,
            x0,
            x1,
            height: dominant.height,
            text: text.trim(),
            hardBreakBefore: hardBreakYs.some((breakY) => Math.abs(breakY - y) <= yTolerance(dominant.height, dominant.height)),
        });
    }
    return lines;
}
//# sourceMappingURL=lines.js.map